<template>
  <div class="ap-page">
    <v-snackbar v-model="showSnackBar" top right :timeout="4500">
      {{ msg }}
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="showSnackBar = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <vue-element-loading :active="appLoading" :is-full-screen="true" background-color="rgba(255,255,255,0.7)" color="#16a34a" spinner="spinner" />

    <section class="ap-hero">
      <div class="ap-hero-copy">
        <span class="ap-kicker">Edit &amp; resubmit<template v-if="list.applicationNo"> · No. {{ list.applicationNo }}</template></span>
        <h1 class="ap-hero-title">Update your <em>application.</em></h1>
        <p class="ap-hero-sub">Make the changes the division office asked for, then resubmit. It goes back to the Section Clerk for review.</p>
      </div>
      <div class="ap-hero-actions">
        <button type="button" class="ap-btn ap-btn--glass" @click="$router.go(-1)">
          <v-icon small>mdi-arrow-left</v-icon>
          Back
        </button>
      </div>
    </section>

    <div v-if="list.reason && list.reason !== 'Nill'" class="ap-notice ap-notice--warning mb-4">
      <v-icon small>mdi-message-reply-text-outline</v-icon>
      <span><strong>Remarks from the division office:</strong> {{ list.reason }}</span>
    </div>

    <!-- 01 Applicant -->
    <section class="ap-card">
      <div class="ap-card-head">
        <div class="ap-card-heading">
          <span class="ap-card-no">1</span>
          <h2 class="ap-card-title">Applicant details</h2>
        </div>
      </div>
      <div class="ap-card-body">
        <div class="ap-grid">
          <v-text-field v-model="list.applicantName" outlined dense hide-details label="Name of the applicant"></v-text-field>
          <v-text-field v-model="list.applicantMobileNo" :rules="PhoneRules" type="number" class="inputPrice" outlined dense hide-details="auto" label="Mobile number"></v-text-field>
          <v-text-field v-model="list.applicantEmail" :rules="emailRules" outlined dense hide-details="auto" label="Email"></v-text-field>
          <v-text-field v-model="list.applicantAddress" outlined dense hide-details label="Address" class="ap-span-2"></v-text-field>
          <v-text-field v-model="list.applicantPinCode" type="number" class="inputPrice" outlined dense hide-details label="Pincode"></v-text-field>
          <v-autocomplete v-model="list.applicantDistrict" :items="districts" outlined dense hide-details label="District"></v-autocomplete>
          <v-autocomplete v-model="list.natureOfApplication" :items="natureOfApplicationitem" outlined dense hide-details label="Nature of application"></v-autocomplete>
          <v-autocomplete v-model="list.idType" :items="idcardtypes" outlined dense hide-details label="ID card type"></v-autocomplete>
          <v-text-field v-model="list.idcardNo" outlined dense hide-details label="ID card number"></v-text-field>
        </div>
      </div>
    </section>

    <!-- 02 Schedule -->
    <section class="ap-card">
      <div class="ap-card-head">
        <div class="ap-card-heading">
          <span class="ap-card-no">2</span>
          <div>
            <h2 class="ap-card-title">Shooting schedule</h2>
            <p class="ap-card-sub">Edit the existing locations or add new ones.</p>
          </div>
        </div>
        <button type="button" class="ap-btn ap-btn--outline ap-btn--sm" @click="addLeaderDetails()">
          <v-icon small>mdi-plus</v-icon>
          Add location
        </button>
      </div>
      <div class="ap-card-body">
        <div v-for="(item, i) in leaderDetails2" :key="'old' + i" class="ap-block">
          <div class="ap-block-head">
            <span class="ap-block-title">Location {{ i + 1 }}</span>
            <button type="button" class="ap-btn ap-btn--danger ap-btn--sm" @click="removeLeaderDetail(i)">
              <v-icon small>mdi-delete-outline</v-icon>
              Remove
            </button>
          </div>
          <div class="ap-grid ap-grid--4">
            <v-text-field v-model="item.location" outlined dense hide-details label="Location" prepend-inner-icon="mdi-map-marker-outline"></v-text-field>
            <v-text-field v-model="item.filmName" outlined dense hide-details label="Film name" prepend-inner-icon="mdi-movie-open-outline"></v-text-field>
            <v-menu v-model="item.startDate" :close-on-content-click="false" max-width="290">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="formatDate(item.fromDate)" outlined dense readonly clearable hide-details label="From date" prepend-inner-icon="mdi-calendar-start" v-bind="attrs" v-on="on" @click:clear="item.fromDate = null"></v-text-field>
              </template>
              <v-date-picker v-model="item.fromDate" color="#16a34a" @change="item.startDate = false"></v-date-picker>
            </v-menu>
            <v-menu v-model="item.endDate" :close-on-content-click="false" max-width="290">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="formatDate(item.toDate)" outlined dense readonly clearable hide-details label="To date" prepend-inner-icon="mdi-calendar-end" v-bind="attrs" v-on="on" @click:clear="item.toDate = null"></v-text-field>
              </template>
              <v-date-picker v-model="item.toDate" :min="item.fromDate" color="#16a34a" @change="item.endDate = false"></v-date-picker>
            </v-menu>
            <v-text-field v-model="item.noOfMembers" type="number" outlined dense hide-details label="Crew members" prepend-inner-icon="mdi-account-group-outline"></v-text-field>
            <v-text-field v-model="item.noOfVehicles" type="number" outlined dense hide-details label="Vehicles" prepend-inner-icon="mdi-car-outline"></v-text-field>
            <v-text-field v-model="item.installationDays" type="number" outlined dense hide-details label="Installation days" prepend-inner-icon="mdi-hammer-wrench"></v-text-field>
          </div>
        </div>

        <div v-for="(item, i) in leaderDetails" :key="'new' + i" class="ap-block">
          <div class="ap-block-head">
            <span class="ap-block-title">New location {{ i + 1 }}</span>
            <button type="button" class="ap-btn ap-btn--danger ap-btn--sm" @click="removeleaderdetails(i)">
              <v-icon small>mdi-delete-outline</v-icon>
              Remove
            </button>
          </div>
          <div class="ap-grid ap-grid--4">
            <v-text-field v-model="item.location" outlined dense hide-details label="Location" prepend-inner-icon="mdi-map-marker-outline"></v-text-field>
            <v-text-field v-model="item.filmName" outlined dense hide-details label="Film name" prepend-inner-icon="mdi-movie-open-outline"></v-text-field>
            <v-menu v-model="item.startDate" :close-on-content-click="false" max-width="290">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="formatDate(item.fromDate)" outlined dense readonly clearable hide-details label="From date" prepend-inner-icon="mdi-calendar-start" v-bind="attrs" v-on="on" @click:clear="item.fromDate = null"></v-text-field>
              </template>
              <v-date-picker v-model="item.fromDate" color="#16a34a" @change="item.startDate = false"></v-date-picker>
            </v-menu>
            <v-menu v-model="item.endDate" :close-on-content-click="false" max-width="290">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="formatDate(item.toDate)" outlined dense readonly clearable hide-details label="To date" prepend-inner-icon="mdi-calendar-end" v-bind="attrs" v-on="on" @click:clear="item.toDate = null"></v-text-field>
              </template>
              <v-date-picker v-model="item.toDate" :min="item.fromDate" color="#16a34a" @change="item.endDate = false"></v-date-picker>
            </v-menu>
            <v-text-field v-model="item.noOfMembers" type="number" outlined dense hide-details label="Crew members" prepend-inner-icon="mdi-account-group-outline"></v-text-field>
            <v-text-field v-model="item.noOfVehicles" type="number" outlined dense hide-details label="Vehicles" prepend-inner-icon="mdi-car-outline"></v-text-field>
            <v-text-field v-model="item.installationDays" type="number" outlined dense hide-details label="Installation days" prepend-inner-icon="mdi-hammer-wrench"></v-text-field>
          </div>
        </div>
      </div>
    </section>

    <!-- 03 Animals -->
    <AnimalsSection
      :number="3"
      :involved.sync="list.animalinvolved"
      :animals="animals"
      :preshoot-number.sync="list.preshootNumber"
      :validity-from.sync="list.validityfromDate"
      :validity-to.sync="list.validitytoDate"
      :min-date="minDatee"
      :file-name="isNewFile(Preshootfile) ? Preshootfile.name : ''"
      :existing-url="list.Preshootfile ? mediaURL + list.Preshootfile : ''"
      @file="Preshootfile = $event"
    />

    <!-- 04 Documents -->
    <section class="ap-card">
      <div class="ap-card-head">
        <div class="ap-card-heading">
          <span class="ap-card-no">4</span>
          <div>
            <h2 class="ap-card-title">Documents</h2>
            <p class="ap-card-sub">Choose a new file only if you need to replace the one you uploaded.</p>
          </div>
        </div>
      </div>
      <div class="ap-card-body">
        <div class="file-row">
          <label class="ap-file" :class="{ 'ap-file--done': isNewFile(idProof) }">
            <span class="ap-file-icon"><v-icon>{{ isNewFile(idProof) ? "mdi-check" : "mdi-card-account-details-outline" }}</v-icon></span>
            <span class="ap-file-text">
              <span class="ap-file-title">ID proof</span>
              <span class="ap-file-hint">{{ isNewFile(idProof) ? idProof.name : "Choose a file to replace it" }}</span>
            </span>
            <input id="idProof" ref="idProof" type="file" accept=".pdf, .jpeg, .jpg, .png" @change="handleFileChange('idProof')" />
          </label>
          <a v-if="list.idProof" :href="mediaURL + list.idProof" target="_blank" rel="noopener" class="ap-btn ap-btn--outline ap-btn--sm">
            <v-icon small>mdi-eye-outline</v-icon>
            View current
          </a>
        </div>

        <div v-if="list.natureOfApplication === 'Non-Commercial photographs/videos'" class="file-row mt-3">
          <label class="ap-file" :class="{ 'ap-file--done': isNewFile(pressmedia) }">
            <span class="ap-file-icon"><v-icon>{{ isNewFile(pressmedia) ? "mdi-check" : "mdi-camera-outline" }}</v-icon></span>
            <span class="ap-file-text">
              <span class="ap-file-title">Press media accreditation</span>
              <span class="ap-file-hint">{{ isNewFile(pressmedia) ? pressmedia.name : "Accredited press media photographers" }}</span>
            </span>
            <input id="pressmedia" ref="pressmedia" type="file" accept=".pdf, .jpeg, .jpg, .png" @change="handleFileChange3('pressmedia')" />
          </label>
          <a v-if="list.additionalProof" :href="mediaURL + list.additionalProof" target="_blank" rel="noopener" class="ap-btn ap-btn--outline ap-btn--sm">
            <v-icon small>mdi-eye-outline</v-icon>
            View current
          </a>
        </div>

        <div v-if="list.natureOfApplication === 'Educational Films/ Photography-Private parties-non-commercial'" class="file-row mt-3">
          <label class="ap-file" :class="{ 'ap-file--done': isNewFile(educationfilmPrivate) }">
            <span class="ap-file-icon"><v-icon>{{ isNewFile(educationfilmPrivate) ? "mdi-check" : "mdi-school-outline" }}</v-icon></span>
            <span class="ap-file-text">
              <span class="ap-file-title">Educational film proof (private)</span>
              <span class="ap-file-hint">{{ isNewFile(educationfilmPrivate) ? educationfilmPrivate.name : "Private parties — non-commercial" }}</span>
            </span>
            <input id="educationfilmPrivate" ref="educationfilmPrivate" type="file" accept=".pdf, .jpeg, .jpg, .png" @change="handleFileChange4('educationfilmPrivate')" />
          </label>
          <a v-if="list.additionalProof" :href="mediaURL + list.additionalProof" target="_blank" rel="noopener" class="ap-btn ap-btn--outline ap-btn--sm">
            <v-icon small>mdi-eye-outline</v-icon>
            View current
          </a>
        </div>

        <div v-if="list.natureOfApplication === 'Educational Films/ Photography- Government agency-non-commercial'" class="file-row mt-3">
          <label class="ap-file" :class="{ 'ap-file--done': isNewFile(educationfilmGov) }">
            <span class="ap-file-icon"><v-icon>{{ isNewFile(educationfilmGov) ? "mdi-check" : "mdi-bank-outline" }}</v-icon></span>
            <span class="ap-file-text">
              <span class="ap-file-title">Educational film proof (government)</span>
              <span class="ap-file-hint">{{ isNewFile(educationfilmGov) ? educationfilmGov.name : "Government agency — non-commercial" }}</span>
            </span>
            <input id="educationfilmGov" ref="educationfilmGov" type="file" accept=".pdf, .jpeg, .jpg, .png" @change="handleFileChange5('educationfilmGov')" />
          </label>
          <a v-if="list.additionalProof" :href="mediaURL + list.additionalProof" target="_blank" rel="noopener" class="ap-btn ap-btn--outline ap-btn--sm">
            <v-icon small>mdi-eye-outline</v-icon>
            View current
          </a>
        </div>
      </div>
    </section>

    <div class="ap-submitbar">
      <span class="ap-submitbar-text">
        <v-icon small color="#d6b97b">mdi-information-outline</v-icon>
        Resubmitting sends the application back to the Section Clerk.
      </span>
      <button type="button" class="ap-btn ap-btn--light" :disabled="appLoading" @click="edit()">
        Save &amp; resubmit
        <v-icon small>mdi-arrow-right</v-icon>
      </button>
    </div>
  </div>
</template>
  
<script>
import axios from "axios";
import moment from "moment";
import AnimalsSection from "@/components/Common/AnimalsSection.vue";
// import CryptoJS from "crypto-js";

export default {
  components: {
    AnimalsSection,
  },
  data() {

    const today = new Date().toISOString().substr(0, 10);
    return {
      animal: ["YES",
        "NO"],
      animalinvolved: '',
      // [{ species, count }] edited by Common/AnimalsSection.vue
      animals: [],
      menu3: false,
      menu4: false,
      noOfAnimal: '',
      preshootNumber: '',
      species: '',
      validityfromDate: '',
      validitytoDate: '',
      Preshootfile: '',
      validitystartDate: null,
      pressmedia: "",
      educationfilmGov: "",
      educationfilmPrivate: "",
      validityendDate: null,
      idProof: {},
      reqLetter: {},
      isAgree: false,
      participantfile: {},
      leaderIdProof: {},
      cdate: {},
      memberDetails: [],
      minDatee: null,
      memberDetails2: [],
      memberdialog: false,
      leaderName: null,
      leaderAddress1: null,
      leaderAddress2: null,
      telephone: null,
      fromDate: new Date(),
      toDate: new Date(),
      idCardType: null,
      leaderPhone: null,
      leaderGender: null,
      leaderEmail: null,
      district: null,
      instHeadName: null,
      list2: {},
      dist: null,
      headDesignation: null,
      idCardTypeidCardType: null,
      idNo: null,
      idcardtypes: [
        "Election Id Card",
        "Driving License",
        "Aadhaar Card",
      ],
      instName: null,
      instCode: null,
      instType: null,
      instAddress: null,
      instTeleNo: null,
      instMobileNo: null,
      instEmail: null,
      institutionTypes: [
        "Government",
        "Aided",
        "Private",
        "Others"
      ],
      startDate: today,
      showSnackBar: false,
      isChecked: false,
      appLoading: false,
      msg: "",
      expandSection: false,
      name: null,
      barlist: [],
      list: {},
      selectedDistrict: null,
      districts: [
        "Thiruvananthapuram",
        "Kollam",
        "Pathanamthitta",
        "Alappuzha",
        "Kottayam",
        "Idukki",
        "Ernakulam",
        "Thrissur",
        "Palakkad",
        "Malappuram",
        "Kozhikode",
        "Wayanad",
        "Kannur",
        "Kasaragod"
      ],
      campDuration: '',
      menu1: false,
      submitdialog: false,
      male: null,
      leaderDetails2: [],
      memberCount: null,
      leaderDetails: [],

      menu2: false,
      applicantCategory: "",
      applicantname: null,
      id:this.$route.query.id,
      natureOfApplicationitem: [
        "Commercial Film",
        "Documentary",
        "TV Serial",
        "Educational Films/ Photography-Private parties-non-commercial",
        "Educational Films/ Photography- Government agency-non-commercial",
        "Educational film - commercial purpose",
        "Commercial Photography",
        "Non-Commercial photographs/videos",
      ],


    };
  },

  // },
  created() {
    this.dist = this.$route.query.district;

  },
  computed: {
    minDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const day = today.getDate();
      return `${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`;
    },
    PhoneRules() {
      return [
        (v) => !!v || "Phone number is required",
        (v) => /^[0-9]{10}$/.test(v) || "Phone number must be 10 digits",
      ];
    },
    emailRules() {
      return [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ];
    },


  },
  mounted() {
    this.getList();
    this.getCircle();

    // this.checkAvailability();
  },


  methods: {
    getCircle() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/get/circles",
        headers: {
          token: localStorage.getItem("token"),
        },
        params: {
        },
      })
        .then((response) => {
          this.circle = response.data.data;

          this.appLoading = false;
        })
        .catch((err) => {
          this.ServerError = true;
          console.log(err);
        });
    },
    submit2() {
      this.cdate = null;
      this.memberdialog = false;
    },
    handleFileChange(field) {
      this[field] = this.$refs[field].files[0];
    },
    handleFileChange2(field) {
      this[field] = this.$refs[field].files[0];
    },
    handleFileChange3(field) {
      this[field] = this.$refs[field].files[0];
    },
    handleFileChange4(field) {
      this[field] = this.$refs[field].files[0];
    },
    handleFileChange5(field) {
      this[field] = this.$refs[field].files[0];
    },
    validateFile(file) {
      return file.size <= 100 * 1024;
    },
    removeMember(index) {
      this.memberDetails2.splice(index, 1);
    },
    removeLeader(index) {
      this.leaderDetails2.splice(index, 1);
    },
    removeLeaderDetail(index) {
      this.leaderDetails2.splice(index, 1);
    },
    checkAvailability(cdate) {
      axios({
        method: "POST",
        url: "/check/availability",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          date: cdate,
          campid: this.campidx,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.list2 = response.data.natureCampDetails;
            this.checkstatus = response.data.data;

            // Check if checkstatus is equal to 0
            if (this.checkstatus === 0) {
              this.memberdialog = true;
            }

            console.log("message is ", this.checkstatus);
          } else {
            this.memberdialog = true;
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },


    // A file the applicant just picked (existing uploads are stored keys).
    isNewFile(file) {
      return !!file && typeof file === "object" && !!file.name;
    },
    addLeaderDetails() {
      this.leaderDetails.push({ location: '', fromDate: '', toDate: '', noOfMembers: '', noOfVehicles: '', filmName: '', installationDays: '', startDate: false, endDate: false },);
      this.location = '';
      this.filmName = '';
      this.fromDate = '';
      this.toDate = '';
      this.noOfMembers = '';
      this.noOfVehicles = '';

    },

    removeValues() {
      this.leaderDetails.pop();
    },
    removeleaderdetails(index) {
      this.leaderDetails.splice(index, 1);
    },
    submit() {
      // const encryptedId = this.$route.query.id;
      // const decryptedId = CryptoJS.AES.decrypt(encryptedId, 'encryptionSecret').toString(CryptoJS.enc.Utf8);
      axios({
        method: "POST",
        url: "/final/submit",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          appid: this.id,

        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.submitdialog = false;
            this.$router.push({ path: "/ForwardedApplications" });
            window.location.reload();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },
    addValues() {
      this.memberDetails.push({ memberGender: '', memberDob: '', memberName: '' });
    },
    removeVehicle(index) {
      this.memberDetails.splice(index, 1);
    },
    edit() {
      this.appLoading = true;
      // const encryptedId = this.$route.query.id;
      // const decryptedId = CryptoJS.AES.decrypt(encryptedId, 'encryptionSecret').toString(CryptoJS.enc.Utf8);
      // Existing rows plus the newly added ones (without mutating the form,
      // so a failed save can be retried without duplicating rows).
      const rows = this.leaderDetails2.concat(
        this.leaderDetails.map((item) => ({ location: item.location, filmName: item.filmName, fromDate: item.fromDate, toDate: item.toDate, noOfMembers: item.noOfMembers, noOfVehicles: item.noOfVehicles, installationDays: item.installationDays }))
      );
      const formData = new FormData();
      formData.append("filmShootingDetails", JSON.stringify(rows));
      // formData.append("nameofOffice", this.list.nameofOffice);
      if(this.list.natureOfApplication == "Non-Commercial photographs/videos") {
        formData.append("additionalProof", this.pressmedia);
      }
      if(this.list.natureOfApplication == "Educational Films/ Photography-Private parties-non-commercial") {
        formData.append("additionalProof", this.educationfilmPrivate);
      }
      if(this.list.natureOfApplication == "Educational Films/ Photography- Government agency-non-commercial") {
        formData.append("additionalProof", this.educationfilmGov);

      }
      formData.append("applicantMobileNo", this.list.applicantMobileNo);
      formData.append("applicantEmail", this.list.applicantEmail);
      formData.append("applicantAddress", this.list.applicantAddress);
      formData.append("applicantDistrict", this.list.applicantDistrict);
      formData.append("applicantPinCode", this.list.applicantPinCode);
      formData.append("idType", this.list.idType);
      formData.append("idcardNo", this.list.idcardNo);
      formData.append("applicantName", this.list.applicantName);
      formData.append("appid", this.id);
      formData.append("divisionid", this.list.divisionid);
      formData.append("idProof", this.idProof);
      formData.append("Preshootfile", this.Preshootfile);
      formData.append("animalinvolved", this.list.animalinvolved);
      if (this.list.animalinvolved === "YES") {
        if (this.animals.some((a) => a.choice === "__other__" && !String(a.species || "").trim())) {
          this.appLoading = false;
          this.msg = "Please type the species name for the animal marked \"Other\".";
          this.showSnackBar = true;
          return;
        }
        const animals = this.animals
          .filter((a) => a.species && String(a.species).trim())
          .map((a) => ({ species: String(a.species).trim(), count: Number(a.count) || 0 }));
        if (!animals.length) {
          this.appLoading = false;
          this.msg = "Please add at least one animal.";
          this.showSnackBar = true;
          return;
        }
        formData.append("animals", JSON.stringify(animals));
        formData.append("species", animals.map((a) => a.species).join(", "));
        formData.append("noOfAnimal", animals.reduce((sum, a) => sum + a.count, 0));
      }
      formData.append("preshootNumber", this.list.preshootNumber);
      formData.append("validityfromDate", this.list.validityfromDate);
      formData.append("validitytoDate", this.list.validitytoDate);
      axios({
        method: "POST",
        url: "/edit/application",
        headers: {
          "Content-Type": "multipart/form-data",
          token: localStorage.getItem("token"),

        },
        data: formData,
      })
        .then((response) => {
          if (response.data.status == true) {
            this.appLoading = false;
            this.$appAlert({ type: "success", title: "Application resubmitted", message: response.data.msg }).then(() => {
              this.$router.push({ path: "/mybooking" });
            });
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          this.appLoading = false;
          this.msg = "Could not save the application. Please try again.";
          this.showSnackBar = true;
          console.error(err);
        });
    },

    getList() {
      this.appLoading = true;
      // const encryptedId = this.$route.query.id;
      // const decryptedId = CryptoJS.AES.decrypt(encryptedId, 'encryptionSecret').toString(CryptoJS.enc.Utf8);
      axios({
        method: "POST",
        url: "/single/application",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          appid: this.id,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.list = response.data.data;
            if (this.list.validityfromDate) {
              this.list.validityfromDate = moment(this.list.validityfromDate).format("YYYY-MM-DD");
            }
            if (this.list.validitytoDate) {
              this.list.validitytoDate = moment(this.list.validitytoDate).format("YYYY-MM-DD");
            }
            if (this.list.animals && this.list.animals.length) {
              this.animals = this.list.animals.map((a) => ({ species: a.species, count: a.count }));
            } else if (this.list.species) {
              this.animals = [{ species: this.list.species, count: Number(this.list.noOfAnimal) || 1 }];
            }
            this.leaderDetails2 = this.list.filmShootingDetails;
            this.leaderDetails2.forEach((detail) => {
              detail.fromDate = moment(detail.fromDate).format("YYYY-MM-DD");
              detail.toDate = moment(detail.toDate).format("YYYY-MM-DD");
            });
            this.idProof = this.list.idProof;
            this.Preshootfile = this.list.Preshootfile;
            this.cdate = this.list.startDate;
            this.cdate = moment(this.cdate).format("YYYY-MM-DD");
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.ServerError = true;
          console.error(err);
        });
    },
    formatDate(date) {
    if (!date) return null;
    const [year, month, day] = date.split('-');
    return `${day}-${month}-${year}`;
  },
  },
};
</script>
<style>
.no-spinners {
  appearance: textfield;
  -moz-appearance: textfield;
  -webkit-appearance: textfield;
}

.inputPrice input::-webkit-outer-spin-button,
.inputPrice input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
</style>

<style scoped>
.file-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.file-row .ap-file {
  flex: 1 1 320px;
}
</style>
